<template>
    <div class="form-field">
        <!-- The control lives in the slot, so the a11y rule can't statically see the
             for/id association FormField provides via context. -->
        <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
        <label :id="labelId" :for="controlId" class="label" :class="hideLabel && 'sr-only'">
            <!-- eslint-disable @intlify/vue-i18n/no-raw-text -- punctuation: the required marker is aria-hidden and has no words to translate -->
            <slot name="label">{{ label }}</slot
            ><span v-if="required" class="required-marker" aria-hidden="true">*</span>
            <!-- eslint-enable @intlify/vue-i18n/no-raw-text -- end of the required-marker exemption -->
        </label>
        <slot></slot>
        <p v-if="hint || $slots.hint" :id="hintId" class="hint">
            <slot name="hint">{{ hint }}</slot>
        </p>
        <p v-if="error || $slots.error" :id="errorId" class="error">
            <slot name="error">{{ error }}</slot>
        </p>
    </div>
</template>

<script lang="ts">
interface FormFieldProps {
    /**
     * Label text, associated with the slotted control via for/id. Required even when the
     * label slot supplies richer content, so every field keeps a plain-text name.
     */
    label: string;
    /** Supporting text under the control, wired into the control's aria-describedby. The hint slot overrides it. */
    hint?: string;
    /** Error message under the control; also marks the control aria-invalid. The error slot overrides it. */
    error?: string;
    /** Renders a required marker next to the label. */
    required?: boolean;
    /** Keeps the label for screen readers only. */
    hideLabel?: boolean;
    /**
     * Sets the control's id. Pass it here rather than on the slotted control, so the label's
     * `for` and the control's `id` stay the same value.
     */
    id?: string;
}
</script>

<script setup lang="ts">
import { computed, onBeforeUpdate, provide, shallowRef, useId, useSlots } from "vue";

import { formFieldContextKey, type FormFieldControlAttrs } from "./formFieldContext";

const props = defineProps<FormFieldProps>();
const slots = useSlots();

const controlId = props.id ?? useId();
const labelId = `${controlId}-label`;
const hintId = `${controlId}-hint`;
const errorId = `${controlId}-error`;

// A hint or error renders when either its prop or its slot is given, and only a rendered
// paragraph may be referenced from aria-describedby. Slots are not reactive, so their
// presence is mirrored into refs that refresh before each re-render (a parent toggling
// `<template v-if="..." #error>` re-renders FormField) for the computeds below to track.
const hasHintSlot = shallowRef(!!slots.hint);
const hasErrorSlot = shallowRef(!!slots.error);
onBeforeUpdate(() => {
    hasHintSlot.value = !!slots.hint;
    hasErrorSlot.value = !!slots.error;
});

const hasHint = computed(() => !!props.hint || hasHintSlot.value);
const hasError = computed(() => !!props.error || hasErrorSlot.value);

const describedBy = computed(() => {
    const ids = [hasHint.value ? hintId : undefined, hasError.value ? errorId : undefined].filter((id): id is string => !!id);
    return ids.length ? ids.join(" ") : undefined;
});
const invalid = computed(() => hasError.value);

const controlAttrs = computed<FormFieldControlAttrs>(() => ({
    id: controlId,
    ...(describedBy.value && { "aria-describedby": describedBy.value }),
    ...(invalid.value && { "aria-invalid": "true" as const }),
}));

provide(formFieldContextKey, { controlId, labelId, describedBy, invalid, controlAttrs });
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .form-field {
        @apply flex w-full flex-col gap-2;
    }

    .form-field .label {
        @apply body-small text-control-label;
    }

    .form-field .required-marker {
        @apply ml-1 text-status-error;
    }

    .form-field .hint {
        @apply body-small text-ink-muted;
    }

    .form-field .error {
        @apply body-small text-status-error;
    }
}
</style>
